import { type Objectish } from "immer";
import { ReplaySubject } from "rxjs";

import { type DoTransactionFn } from "../../metadata-state";
import { observableToAsyncGenerator } from "../../observableToAsyncGenerator";
import { type PageStorageBridge } from "./PageStorageBridge";

export interface PageStorageSubscribable {
	subscribe: (onUpdate: (pageStorage: Objectish) => void) => { unsubscribe: () => void };
	commit: (doTransaction: DoTransactionFn<Objectish>) => Promise<void>;
}

/**
 * A general-purpose bridge between the push-based `PageStorageSubscribable` and the pull-oriented
 * `AsyncIterable`-based runtime.
 *
 * Intuitively, both local and collaborative implementations of page storage use RxJS `Observable`s
 * to "push" values out to subscribers. For the runtime to get these valuses, though, they need to
 * be exposed as pull-based async generator functions. This class simply bridges the two paradigms,
 * turning the `Observable` into an `AsyncIterableIterator`.
 */
export class PageStorageSubscribableBridge implements PageStorageBridge {
	/**
	 * EXTREMELY IMPORTANT: `ReplaySubject` because `pageStorage` needs to emit the current value of
	 * page storage, whenever it subscribes. If you do not do this, the fetch of page storage data
	 * will sometimes complete before the `pageStorage` subscription is created, which causes
	 * `pageStorage` to hang forever in the app. Or at least, hang until you mutate it.
	 */
	private _dataUpdates = new ReplaySubject<Objectish>(1);
	private _clientSubscription: { unsubscribe: () => void };

	constructor(private _subscribable: PageStorageSubscribable) {
		this._subscribable = _subscribable;
		this._clientSubscription = this._subscribable.subscribe((data) => {
			this._dataUpdates.next(data);
		});
	}

	get data(): AsyncIterableIterator<Objectish> {
		async function* lastResultThenValues(pageStorageBridge: PageStorageSubscribableBridge) {
			yield* observableToAsyncGenerator(pageStorageBridge._dataUpdates);
		}

		return lastResultThenValues(this);
	}

	commit = async (doTransaction: DoTransactionFn<Objectish>): Promise<void> => {
		await this._subscribable.commit(doTransaction);
	};

	dispose() {
		this._dataUpdates.complete();
		this._clientSubscription?.unsubscribe();
	}
}
